import type { MonthDayRecord, MonthGridCell, MonthPerfTier } from './types';
import { MONTH_GOAL } from './monthMockData';

export const MONTH_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const MONTH_TIER: Record<
  MonthPerfTier,
  { fg: string; tint: string; bar: string }
> = {
  great: {
    fg: 'var(--success)',
    tint: '#C9E8DE',
    bar: 'var(--success)',
  },
  good: {
    fg: 'var(--success)',
    tint: '#DCEFE9',
    bar: '#139172',
  },
  soft: {
    fg: 'var(--gold)',
    tint: '#F5E4BD',
    bar: 'var(--gold)',
  },
  problem: {
    fg: 'var(--terracotta)',
    tint: '#F3D4CF',
    bar: 'var(--terracotta)',
  },
  none: {
    fg: 'var(--ink-3)',
    tint: 'var(--cream-1)',
    bar: 'var(--ink-4)',
  },
};

/** Build a Sun–Sat calendar grid (35/42 cells) including off-month days. */
export function buildMonthGrid(year: number, month: number): MonthGridCell[] {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const cells: MonthGridCell[] = [];

  for (let i = firstWeekday - 1; i >= 0; i--) {
    cells.push({ dayNum: prevDays - i, off: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ dayNum: d, off: false, dow: new Date(year, month, d).getDay() });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ dayNum: cells.length - daysInMonth - firstWeekday + 1, off: true });
  }

  return cells;
}

export function monthPerfTier(rec: MonthDayRecord | null | undefined): MonthPerfTier {
  if (!rec) return 'none';
  const r = rec.production / (rec.goal || MONTH_GOAL);
  if (r >= 1.15) return 'great';
  if (r >= 1.0) return 'good';
  if (r >= 0.7) return 'soft';
  return 'problem';
}

/**
 * Cell tint for the Options → "Day heatmap" metric. `goal` is the production-vs-goal tier;
 * `new` ranks days against the busiest new-patient day of the month; `noshow` is greener the fewer
 * no-shows. Projected days have no real counts yet, so they stay neutral for the count metrics.
 */
export function monthHeatTier(
  rec: MonthDayRecord | null | undefined,
  metric: 'goal' | 'new' | 'noshow',
  maxNewPatients: number,
): MonthPerfTier {
  if (!rec) return 'none';
  if (metric === 'goal') return monthPerfTier(rec);
  if (rec.projected) return 'none';

  if (metric === 'new') {
    if (rec.newPatients <= 0) return 'none';
    const ratio = rec.newPatients / Math.max(maxNewPatients, 1);
    if (ratio >= 0.66) return 'great';
    if (ratio >= 0.33) return 'good';
    return 'soft';
  }

  if (rec.noShows <= 0) return 'great';
  if (rec.noShows === 1) return 'good';
  if (rec.noShows === 2) return 'soft';
  return 'problem';
}

export function monthMoneyShort(v: number): string {
  return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
}

export function monthDayAtRisk(rec: MonthDayRecord): number {
  return (rec.noShows || 0) + (rec.cancellations || 0);
}

export function monthDayHasIssue(rec: MonthDayRecord): boolean {
  return Boolean(rec.anomaly) || monthDayAtRisk(rec) >= 5;
}

export function monthDayUnderGoal(rec: MonthDayRecord): boolean {
  return rec.production / (rec.goal || MONTH_GOAL) < 1;
}

/** Month options drawer — highlight filter. */
export function monthCellMatchesFilter(
  rec: MonthDayRecord | null,
  filter: string,
): boolean {
  if (filter === 'all' || !rec) return true;
  if (rec.highlighted !== undefined) return rec.highlighted;
  if (filter === 'under') return monthDayUnderGoal(rec);
  if (filter === 'issues') return monthDayHasIssue(rec);
  return true;
}
